import { Toggle, Flex, Input, Icon } from '@kognitos/lattice';
import { useState } from 'react';
import type { ControllerRenderProps, FieldValues } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { getNoAutofillProps } from '@/shared/utils';

export interface CredentialSensitiveValueInputProps<
  TFieldValues extends FieldValues,
  TName extends
    import('react-hook-form').Path<TFieldValues> = import('react-hook-form').Path<TFieldValues>,
> {
  field: ControllerRenderProps<TFieldValues, TName>;
  placeholder?: string;
  disabled?: boolean;
  readOnly?: boolean;
  className?: string;
  'data-testid'?: string;
}

export function CredentialSensitiveValueInput({
  className,
  placeholder,
  disabled,
  readOnly,
  field,
  'data-testid': dataTestId,
}: CredentialSensitiveValueInputProps<FieldValues, string>) {
  const { t } = useTranslation('integrations');
  const [inputType, setInputType] = useState('password');
  const [showSensitiveValue, setShowSensitiveValue] = useState(false);

  return (
    <Flex align="center" gap={2} className={className}>
      <Input
        data-testid={dataTestId}
        type={inputType}
        placeholder={placeholder}
        disabled={disabled}
        readOnly={readOnly}
        {...field}
        {...getNoAutofillProps({ sensitive: true })}
        value={field.value || ''}
        className="full-width"
      />
      <Toggle
        data-slot="toggle"
        pressed={showSensitiveValue}
        onPressedChange={pressed => {
          setShowSensitiveValue(pressed);
          setInputType(pressed ? 'text' : 'password');
        }}
        aria-label={t('integrations.customCredentials.showSensitiveValue')}
        variant="outline"
        className="h-10"
      >
        <Icon type={showSensitiveValue ? 'EyeOff' : 'Eye'} />
      </Toggle>
    </Flex>
  );
}
